<template>
  <b-row class="reportsActionsBar mb-3">
    <b-col sm="3" class="mb-2">
      <div class="mb-2 filtersHeader">
        <div>Filters</div>
        <a href="#" @click="showStatusInfoModal = true" class="statusInfoBtn text-info">
          <BIconInfoCircle />
          <span>Statuses Info</span>
        </a>
      </div>
      <b-form-select v-model="selectedReportStatus" :options="reportStatuses" class="mb-2" />
      <b-form-select v-model="selectedProvider" :options="providers" class="mb-2" />
      <div v-if="!uid" class="mb-2 relative">
        <b-form-input v-model="userIdStr" placeholder="Search by user ID" @keydown="searchByUserId" />
        <BIconX v-if="userIdStr" @click="clearUserIdStr" class="reportFilterInputButton" />
      </div>
      <div v-if="!uid" class="mb-2 relative">
        <b-form-input v-model="customerIdStr" placeholder="Search by customer ID" @keydown="searchByCustomerId" />
        <BIconX v-if="customerIdStr" @click="clearCustomerIdStr" class="reportFilterInputButton" />
      </div>
      <div class="mb-2 relative">
        <b-form-input v-model="activationIdStr" placeholder="Search by activation ID" @keydown="searchByActivationId" />
        <BIconX v-if="activationIdStr" @click="clearActivationIdStr" class="reportFilterInputButton" />
      </div>
    </b-col>

    <b-col sm="2" class="mb-2">
      <label class="mb-2 filterHeader">
        <span>Processing Status</span>
        <button
          v-if="selectedProcessingStatuses.length > 0"
          @click="clearProcessingStatusSelection"
          class="clearFilterBtn text-warning"
        >
          &#x2715;
        </button>
      </label>
      <div class="filtersWrap">
        <b-form-checkbox-group
          v-model="selectedProcessingStatuses"
          @change="handleSelectProcessingStatus"
          :options="processingStatuses"
        />
      </div>
    </b-col>

    <b-col sm="2" class="mb-2">
      <label class="mb-2 filterHeader">
        <span>Provider Status</span>
        <button
          v-if="selectedProviderStatuses.length > 0"
          @click="clearProviderStatusSelection"
          class="clearFilterBtn text-warning"
        >
          &#x2715;
        </button>
      </label>
      <div class="filtersWrap">
        <b-form-checkbox-group
          v-model="selectedProviderStatuses"
          @change="handleSelectProviderStatus"
          :options="providerStatuses"
        />
      </div>
    </b-col>

    <b-col sm="1" class="mb-2">
      <label class="mb-2 filterHeader">
        <span>Assigned to</span>
        <button
          v-if="!(selectedAssignedTo.length === 1 && selectedAssignedTo[0] === assignedTo[0].value)"
          @click="setDefaultAssignedToSelection"
          class="clearFilterBtn text-warning"
        >
          &#x2715;
        </button>
      </label>
      <div class="filtersWrap">
        <b-form-checkbox-group v-model="selectedAssignedTo" @change="handleSelectAssignedTo" :options="assignedTo" />
      </div>
    </b-col>

    <b-col sm="2" class="mb-2">
      <label class="mb-2 filterHeader">
        <span>Processed by</span>
        <button
          v-if="selectedProcessedBy.length > 0"
          @click="clearProcessedBySelection"
          class="clearFilterBtn text-warning"
        >
          &#x2715;
        </button>
      </label>
      <div class="filtersWrap">
        <b-form-checkbox-group v-model="selectedProcessedBy" @change="handleSelectProcessedBy" :options="processBy" />
      </div>
    </b-col>

    <b-col sm="2" class="mb-2 text-start text-sm-end">
      <div class="mb-2">Actions</div>
      <div class="mb-2">
        <b-button @click="showAssignStatusModal = true" variant="outline-primary" :disabled="checkedReports.size === 0">
          Assign Status
        </b-button>
      </div>
      <div class="mb-2">
        <b-button @click="createCsv" variant="outline-secondary" :disabled="checkedReports.size === 0">
          Create CSV
        </b-button>
      </div>
      <div class="mb-2">
        <b-button @click="createJson" variant="outline-secondary" :disabled="checkedReports.size === 0">
          Create JSON
        </b-button>
      </div>
      <div class="mb-2">
        <b-button @click="$emit('fetchReportsAfterStatusUpdate')" variant="outline-secondary">
          Refresh Reports
        </b-button>
      </div>
    </b-col>
  </b-row>
  <AssignStatusModal
    :show-assign-status-modal="showAssignStatusModal"
    :checked-reports="checkedReports"
    @hide-assign-status-modal="showAssignStatusModal = false"
    @set-reports-status-data-after-update="setReportsStatusDataAfterUpdate"
    @clear-checked-reports="$emit('clearCheckedReports')"
  />
  <StatusInfoModal
    :show-status-info-modal="showStatusInfoModal"
    @close-status-info-modal="showStatusInfoModal = false"
  />
</template>

<script>
import { BIconX, BIconInfoCircle } from 'bootstrap-icons-vue'
import { BRow } from 'bootstrap-vue-3'
import AssignStatusModal from '@/components/Reports/AssignStatusModal.vue'
import { useReport } from '@/use/report'
import { useCopy } from '@/use/copy'
import { reportStatuses, processingStatuses, processBy, providerStatuses } from '@/constants/report'
import StatusInfoModal from '@/components/Reports/StatusInfoModal.vue'

export default {
  components: {
    StatusInfoModal,
    AssignStatusModal,
    BRow,
    BIconX,
    BIconInfoCircle
  },
  emits: [
    'handleChangeProvider',
    'handleChangeReportStatus',
    'searchByUserId',
    'searchByCustomerId',
    'searchByActivationId',
    'filterByProcessingStatus',
    'fetchReportsAfterStatusUpdate',
    'setReportsStatusDataAfterUpdate',
    'filterByAssignedTo',
    'filterByProcessedBy',
    'filterByProviderStatus',
    'clearCheckedReports'
  ],
  props: {
    providers: {
      default: []
    },
    uid: {
      default: ''
    },
    activationId: {
      default: ''
    },
    checkedReports: {
      default: new Map()
    }
  },
  data: () => {
    return {
      selectedReportStatus: reportStatuses[1].value,
      selectedProvider: 0,
      userIdStr: '',
      customerIdStr: '',
      activationIdStr: '',
      selectedProcessingStatuses: [],
      selectedProviderStatuses: [],
      selectedAssignedTo: [processBy[0].value],
      selectedProcessedBy: [],
      showAssignStatusModal: false,
      reportStatuses,
      processingStatuses,
      processBy,
      assignedTo: processBy,
      providerStatuses,
      showStatusInfoModal: false
    }
  },
  watch: {
    selectedProvider(newProviderId) {
      this.$emit('handleChangeProvider', newProviderId)
    },
    activationId(newActivationId) {
      this.activationIdStr = newActivationId
    },
    selectedReportStatus(newReportStatus) {
      this.$emit('handleChangeReportStatus', newReportStatus)
    }
  },
  setup() {
    const { getCsvData, getJsonData } = useReport()
    const { copyToCb } = useCopy()
    return {
      getCsvData,
      getJsonData,
      copyToCb
    }
  },
  methods: {
    searchByUserId(event) {
      if (event.key === 'Enter') {
        this.$emit('searchByUserId', this.userIdStr)
      }
    },
    clearUserIdStr() {
      this.userIdStr = ''
      this.$emit('searchByUserId', this.userIdStr)
    },
    searchByCustomerId(event) {
      if (event.key === 'Enter') {
        this.$emit('searchByCustomerId', this.customerIdStr)
      }
    },
    clearCustomerIdStr() {
      this.customerIdStr = ''
      this.$emit('searchByCustomerId', this.customerIdStr)
    },
    searchByActivationId(event) {
      if (event.key === 'Enter') {
        this.$emit('searchByActivationId', this.activationIdStr)
      }
    },
    clearActivationIdStr() {
      this.activationIdStr = ''
      this.$emit('searchByActivationId', this.activationIdStr)
    },
    handleSelectProcessingStatus() {
      this.$emit('filterByProcessingStatus', this.selectedProcessingStatuses)
    },
    handleSelectAssignedTo() {
      this.$emit('filterByAssignedTo', this.selectedAssignedTo)
    },
    handleSelectProcessedBy() {
      this.$emit('filterByProcessedBy', this.selectedProcessedBy)
    },
    clearProcessingStatusSelection() {
      this.selectedProcessingStatuses = []
      this.$emit('filterByProcessingStatus', this.selectedProcessingStatuses)
    },
    handleSelectProviderStatus() {
      this.$emit('filterByProviderStatus', this.selectedProviderStatuses)
    },
    setDefaultAssignedToSelection() {
      this.selectedAssignedTo = [processBy[0].value]
      this.$emit('filterByAssignedTo', this.selectedAssignedTo)
    },
    clearProcessedBySelection() {
      this.selectedProcessedBy = []
      this.$emit('filterByProcessedBy', this.selectedProcessedBy)
    },
    clearProviderStatusSelection() {
      this.selectedProviderStatuses = []
      this.$emit('filterByProviderStatus', this.selectedProviderStatuses)
    },
    createCsv() {
      const checkedReportsArr = Array.from(this.checkedReports.values())
      const csvString = this.getCsvData(checkedReportsArr)
      const blob = new Blob([csvString], { type: 'text/csv' })
      this.downloadFile(blob, 'csv')
    },
    createJson() {
      const checkedReportsArr = Array.from(this.checkedReports.values())
      const checkedReportsData = checkedReportsArr.length > 1 ? checkedReportsArr : checkedReportsArr[0]
      const jsonString = this.getJsonData(checkedReportsData)
      const blob = new Blob([jsonString], { type: 'application/json' })
      this.downloadFile(blob, 'json')
    },
    downloadFile(blob, fileExt) {
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `reports-${Date.now()}.${fileExt}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    setReportsStatusDataAfterUpdate(status, reportsIds, statusType) {
      this.$emit('setReportsStatusDataAfterUpdate', status, reportsIds, statusType)
    }
  }
}
</script>

<style lang="scss">
.reportsActionsBar {
  border-bottom: 1px solid #ccc;

  .reportFilterInputButton {
    cursor: pointer;
    position: absolute;
    right: 8px;
    top: 8px;
    width: 22px;
    height: 22px;
  }
  .filterHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .clearFilterBtn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    padding: 2px 2px 0;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .filtersWrap {
    border: 1px solid #ccc;
    border-radius: 5px;
    padding: 10px;
    & > div > div {
      margin-top: 6px;
      margin-bottom: 6px;
    }
  }
  .filtersHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .statusInfoBtn {
      display: flex;
      align-items: center;
      text-decoration: none;
      gap: 5px;
      cursor: pointer;
    }
  }
}
</style>
